import { StateService } from '@infra/state/state.service';
import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, type OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { type NotificationDTO, type NotificationListRequestDTO } from '@api/models';
import { NotificationService } from '@api/services/notification.service';
import { type NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { TypedTableRowDirective } from '@common/directives/typed-table-row.directive';
import { AvatarByIdPipe } from '@shared/pipes/avatar-by-id.pipe';
import { UserByIdPipe } from '@shared/pipes/user-by-id.pipe';
import { AvatarComponent } from '@common/ui/avatar/avatar.component';
import { ButtonPrimaryComponent } from '@common/ui/buttons/button-primary/button-primary.component';
import { ButtonSecondaryComponent } from '@common/ui/buttons/button-secondary/button-secondary.component';
import { CardEmptyComponent } from '@common/ui/card/card-empty.component';
import { ModalComponent } from '@common/ui/modal/modal.component';
import { ToggleComponent } from '@common/ui/toggle/toggle.component';
import { type LazyLoadEvent, PrimeTemplate } from 'primeng/api';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, type TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, map, of, Subject, switchMap } from 'rxjs';

@Component({
  selector: 'app-agent-notifications-overview-modal',
  templateUrl: './notifications-overview-modal.component.html',
  styleUrl: './notifications-overview-modal.component.scss',
  imports: [
    AsyncPipe,
    AvatarByIdPipe,
    AvatarComponent,
    ButtonPrimaryComponent,
    ButtonSecondaryComponent,
    CardEmptyComponent,
    FormsModule,
    ModalComponent,
    NgbDropdownModule,
    PrimeTemplate,
    SkeletonModule,
    TableModule,
    ToggleComponent,
    TooltipModule,
    TranslatePipe,
    TypedTableRowDirective,
    UserByIdPipe,
  ],
})
export class AgentNotificationsOverviewModalComponent implements OnInit {
  private readonly notificationService = inject(NotificationService);
  private readonly stateService = inject(StateService);

  modal = input.required<NgbActiveModal>();

  table = viewChild.required(Table);

  readonly pageSize = 10;
  readonly loadingRows = [...new Array(this.pageSize).keys()];

  notifications = signal<NotificationDTO[]>([]);
  totalNotifications = signal<number>(0);
  initialLoading = signal<boolean>(true);
  loadingNotifications = signal<boolean>(true);
  includeRead = signal<boolean>(false);
  onlyUnread = computed(() => !this.includeRead());

  private readonly unreadNotificationCountState = toSignal(this.stateService.unreadNotificationCount$);
  unreadNotificationCount = computed(() => this.unreadNotificationCountState() ?? 0);

  private readonly listRequests = new Subject<NotificationListRequestDTO>();
  private loadedFirst = 0;
  private loadedOnlyUnread = true;

  constructor() {
    // switchMap cancels a superseded request, so an older response can't overwrite a newer one.
    this.listRequests
      .pipe(
        switchMap(request =>
          this.notificationService.list(request, true).pipe(
            map(response => ({ request, response })),
            catchError(() => of({ request, response: null }))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ request, response }) => {
        if (response) {
          this.notifications.set(response.data);
          this.totalNotifications.set(response.total);
          this.loadedFirst = request.first ?? 0;
          this.loadedOnlyUnread = request.onlyUnread ?? false;
        } else {
          // Keep the paginator and filter on the page whose rows are still shown.
          this.table().first = this.loadedFirst;
          this.includeRead.set(!this.loadedOnlyUnread);
        }

        this.loadingNotifications.set(false);
        this.initialLoading.set(false);
      });
  }

  ngOnInit(): void {
    this.refreshUnreadNotificationCount();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.loadingNotifications.set(true);

    const request = {
      first: event.first,
      rows: event.rows,
      sortField: 'addedDateTime',
      sortOrder: -1,
      onlyUnread: this.onlyUnread(),
    } satisfies NotificationListRequestDTO;

    this.listRequests.next(request);
  }

  onFilterChanged(): void {
    this.reload(true);
  }

  onOpenClick(notification: NotificationDTO, openInNewTab: boolean): void {
    const isUnread = !notification.readDateTime;

    this.notificationService.openNotification(notification, openInNewTab).subscribe(() => {
      if (!isUnread) return;

      this.refreshUnreadNotificationCount();

      if (openInNewTab) {
        this.updateReadDateTime([notification.id], new Date());
      }
    });

    if (!openInNewTab) {
      this.modal().close();
    }
  }

  onToggleReadClick(notification: NotificationDTO): void {
    const isUnread = !notification.readDateTime;
    const request = isUnread
      ? this.notificationService.read(notification.id)
      : this.notificationService.unread(notification.id);

    request.subscribe(() => {
      this.updateReadDateTime([notification.id], isUnread ? new Date() : null);
      this.refreshUnreadNotificationCount();
    });
  }

  onMarkAllReadClick(): void {
    this.notificationService.readAll().subscribe(() => {
      this.stateService.setUnreadNotificationCount(0);

      // Reload instead of patching rows locally: this also replaces a page request that read the rows before the update.
      this.reload(this.onlyUnread());
    });
  }

  onClose(): void {
    this.modal().dismiss();
  }

  private updateReadDateTime(notificationIds: string[], readDateTime: Date | null): void {
    if (this.onlyUnread()) {
      this.reload(false);
      return;
    }

    this.notifications.update(notifications =>
      notifications.map(x => (notificationIds.includes(x.id) ? { ...x, readDateTime } : x))
    );
  }

  private refreshUnreadNotificationCount(): void {
    this.notificationService.countUnread().subscribe(count => {
      this.stateService.setUnreadNotificationCount(count);
    });
  }

  private reload(resetPage: boolean): void {
    const tableElement = this.table();
    const isOnlyRowOnLaterPage = this.onlyUnread() && this.notifications().length === 1 && tableElement.first > 0;
    if (resetPage || isOnlyRowOnLaterPage) {
      tableElement.first = resetPage ? 0 : tableElement.first - this.pageSize;
    }

    const lazyLoadEvent = tableElement.createLazyLoadMetadata() as LazyLoadEvent;
    this.onLazyLoad(lazyLoadEvent);
  }
}
